// External
import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Checkbox, IconButton, ToggleSwitch } from "@toddle-edu/ds-web";
import {
  ChevronDownOutlined,
  ChevronUpOutlined,
  DeleteOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";

// Internal
import DragNDropSortableList from "AppComponents/DragNDropSortableList";
import InlineEditText from "TranscriptsV3/components/InlineEditText";
import { useI18n } from "Hooks";

// Styles
import classes from "./DragAndDropListBox.module.scss";

const TEST_ID_PREFIX = "transcriptsV3-academicRecords-dragAndDropListBox";

const DragAndDropListBox = ({
  items,
  onReorder,
  onToggle,
  title,
  manageColumnsContent,
  onLabelUpdate,
  onDelete,
  showSettings,
  showManageColumns,
  enableReorder,
  showDragHandle,
  enableInlineEdit,
  onSettingsClick,
  onChildToggle,
  showChildItems,
  showHeaderCheckbox,
  onHeaderToggle,
  isHeaderChecked,
}) => {
  const { t } = useI18n(["common"]);
  const [expandedRows, setExpandedRows] = useState({});
  const [activeDragItem, setActiveDragItem] = useState(null);
  const shouldEnableReorder = enableReorder && _.size(items) > 1;
  const shouldShowDragHandle = showDragHandle && shouldEnableReorder;

  useEffect(() => {
    setExpandedRows(prevExpandedRows =>
      _.reduce(
        items,
        (result, item) => {
          if (!_.isEmpty(item.children)) {
            result[item.id] = _.get(prevExpandedRows, item.id, true);
          }
          return result;
        },
        {}
      )
    );
  }, [items]);

  const onToggleExpanded = useCallback(itemId => {
    setExpandedRows(prev => ({
      ...prev,
      [itemId]: !_.get(prev, itemId, true),
    }));
  }, []);

  const getLabelSaveHandler = useCallback(
    itemId => updatedLabel => {
      onLabelUpdate(itemId, updatedLabel);
    },
    [onLabelUpdate]
  );

  const getExpandHandler = useCallback(
    itemId => () => {
      onToggleExpanded(itemId);
    },
    [onToggleExpanded]
  );

  const getToggleHandler = useCallback(
    itemId => () => {
      onToggle(itemId);
    },
    [onToggle]
  );

  const getChildToggleHandler = useCallback(
    (itemId, childId) => () => {
      onChildToggle(itemId, childId);
    },
    [onChildToggle]
  );

  const getDeleteHandler = useCallback(
    itemId => () => {
      onDelete(itemId);
    },
    [onDelete]
  );

  const handleHeaderToggleChange = useCallback(
    event => {
      onHeaderToggle(_.get(event, "target.checked", false));
    },
    [onHeaderToggle]
  );

  const getListItemClassName = useCallback(
    ({ isDraggable, isLastItemWithoutFooter, hasChildren }) =>
      classNames(classes.listItem, {
        [classes.listItemDraggable]: isDraggable,
        [classes.listItemNoDraggable]: !isDraggable,
        [classes.lastItemWithoutFooter]: isLastItemWithoutFooter,
        [classes.listItemWithChildren]: hasChildren,
      }),
    []
  );

  const listContainerClassName = classNames(classes.listContainer, {
    [classes.listContainerNoDraggable]: !shouldEnableReorder,
  });
  const containerClassName = classNames(classes.container, {
    [classes.containerWithoutFooter]: !showManageColumns,
  });
  const rowLabelClassName = classNames("text-body", classes.rowLabel);
  const descriptionClassName = classNames("text-body-s", classes.description);
  const childLabelClassName = classNames("text-body", classes.childLabel);

  const renderLabel = item => {
    if (enableInlineEdit && item.isLabelEditable !== false) {
      return (
        <InlineEditText
          value={item.content}
          onSave={getLabelSaveHandler(item.id)}
          testId={`${TEST_ID_PREFIX}-${item.id}`}
        >
          <InlineEditText.Text className={rowLabelClassName} />
          <InlineEditText.EditButton
            buttonProps={{
              isCompact: true,
              className: classes.editButton,
              testId: `${TEST_ID_PREFIX}-${item.id}-edit`,
            }}
          />
        </InlineEditText>
      );
    }

    return <div className={rowLabelClassName}>{item.content}</div>;
  };

  const renderRow = item => {
    const hasChildren = showChildItems && !_.isEmpty(item.children);
    const totalChildren = hasChildren ? _.size(item.children) : 0;
    const enabledChildrenCount = hasChildren
      ? _.size(
          _.filter(item.children, child => _.get(child, "isEnabled", false))
        )
      : 0;
    const isParentCheckedFromChildren =
      hasChildren &&
      totalChildren > 0 &&
      enabledChildrenCount === totalChildren;
    const isParentIndeterminateFromChildren =
      hasChildren &&
      enabledChildrenCount > 0 &&
      enabledChildrenCount < totalChildren;
    const isExpanded = _.get(expandedRows, item.id, true);
    const isDragging = !_.isNil(activeDragItem);
    const shouldShowChildren = hasChildren && isExpanded && !isDragging;
    const rowContentClassName = classNames(classes.rowContent, {
      [classes.rowContentDraggable]: shouldShowDragHandle,
      [classes.rowContentWithChildren]: hasChildren,
    });
    const leftSectionClassName = classNames(classes.leftSection, {
      [classes.leftSectionNoDragHandle]: !shouldShowDragHandle,
    });
    const labelWrapperClassName = classNames(classes.labelWrapper, {
      [classes.labelWrapperDraggable]: shouldShowDragHandle,
    });

    return (
      <>
        <div className={rowContentClassName}>
          <div className={leftSectionClassName}>
            {shouldShowDragHandle ? (
              <DragNDropSortableList.DragHandle
                id={item.id}
                className={classes.dragHandle}
              />
            ) : null}

            <div className={labelWrapperClassName}>
              {renderLabel(item)}
              {item.description ? (
                <div className={descriptionClassName}>{item.description}</div>
              ) : null}
            </div>
          </div>

          <div className={classes.rightSection}>
            {hasChildren ? (
              <IconButton
                icon={
                  shouldShowChildren ? (
                    <ChevronUpOutlined />
                  ) : (
                    <ChevronDownOutlined />
                  )
                }
                type="plain"
                variant="neutral"
                isCompact
                onClick={getExpandHandler(item.id)}
                testId={`${TEST_ID_PREFIX}-${item.id}-expand`}
                aria-label={
                  shouldShowChildren ? t("common:collapse") : t("common:expand")
                }
              />
            ) : null}
            {showManageColumns && item.isManageColumnItem ? (
              <IconButton
                icon={<DeleteOutlined />}
                type="plain"
                variant="neutral"
                isCompact
                onClick={getDeleteHandler(item.id)}
                testId={`${TEST_ID_PREFIX}-${item.id}-delete`}
                aria-label={t("common:delete")}
              />
            ) : null}
            <Checkbox
              isChecked={
                hasChildren
                  ? isParentCheckedFromChildren
                  : _.get(item, "isEnabled", false)
              }
              isIndeterminate={
                hasChildren
                  ? isParentIndeterminateFromChildren
                  : _.get(item, "isIndeterminate", false)
              }
              onChange={getToggleHandler(item.id)}
              disabled={item.isCheckable === false}
              testId={`${TEST_ID_PREFIX}-${item.id}-checkbox`}
            />
          </div>
        </div>

        {shouldShowChildren ? (
          <div className={classes.childrenContainer}>
            {_.map(item.children, child => (
              <div key={child.id} className={classes.childRow}>
                <div className={childLabelClassName}>{child.content}</div>
                <Checkbox
                  isChecked={child.isEnabled}
                  onChange={getChildToggleHandler(item.id, child.id)}
                  disabled={child.isCheckable === false}
                  testId={`${TEST_ID_PREFIX}-${item.id}-${child.id}-checkbox`}
                />
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  const renderListItem = (item, index, isDraggable) => {
    const isLastItemWithoutFooter =
      !showManageColumns && index === items.length - 1;
    const hasChildren = showChildItems && !_.isEmpty(item.children);
    const itemClassName = getListItemClassName({
      isDraggable,
      isLastItemWithoutFooter,
      hasChildren,
    });

    if (isDraggable) {
      return (
        <DragNDropSortableList.ListItem
          key={item.id}
          id={item.id}
          data={item}
          className={itemClassName}
          isDragOverlayUsed={false}
        >
          {renderRow(item)}
        </DragNDropSortableList.ListItem>
      );
    }

    return (
      <div key={item.id} className={itemClassName}>
        {renderRow(item)}
      </div>
    );
  };

  return (
    <div className={containerClassName}>
      <div className={classes.header}>
        <div className="text-label">{title}</div>
        <div className={classes.headerActions}>
          {showSettings ? (
            <IconButton
              icon={<SettingsOutlined />}
              type="plain"
              variant="neutral"
              isCompact
              onClick={onSettingsClick}
              testId={`${TEST_ID_PREFIX}-settings`}
              aria-label={t("common:settings")}
            />
          ) : null}
          {showHeaderCheckbox ? (
            <ToggleSwitch
              dsVersion="2.0"
              checked={isHeaderChecked}
              onChange={handleHeaderToggleChange}
            />
          ) : null}
        </div>
      </div>

      <div className={listContainerClassName}>
        {shouldEnableReorder ? (
          <DragNDropSortableList
            items={items}
            onReorder={onReorder}
            setActive={setActiveDragItem}
          >
            {_.map(items, (item, index) => renderListItem(item, index, true))}
          </DragNDropSortableList>
        ) : (
          _.map(items, (item, index) => renderListItem(item, index, false))
        )}
      </div>

      {showManageColumns ? (
        <div className={classes.footer}>{manageColumnsContent}</div>
      ) : null}
    </div>
  );
};

DragAndDropListBox.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      isEnabled: PropTypes.bool,
      isIndeterminate: PropTypes.bool,
      description: PropTypes.string,
      isLabelEditable: PropTypes.bool,
      isCheckable: PropTypes.bool,
      isManageColumnItem: PropTypes.bool,
    })
  ),
  onReorder: PropTypes.func,
  onToggle: PropTypes.func,
  title: PropTypes.string,
  manageColumnsContent: PropTypes.node,
  onLabelUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  showSettings: PropTypes.bool,
  showManageColumns: PropTypes.bool,
  enableReorder: PropTypes.bool,
  showDragHandle: PropTypes.bool,
  enableInlineEdit: PropTypes.bool,
  onSettingsClick: PropTypes.func,
  onChildToggle: PropTypes.func,
  showChildItems: PropTypes.bool,
  showHeaderCheckbox: PropTypes.bool,
  onHeaderToggle: PropTypes.func,
  isHeaderChecked: PropTypes.bool,
};

DragAndDropListBox.defaultProps = {
  items: [],
  onReorder: _.noop,
  onToggle: _.noop,
  title: "",
  manageColumnsContent: null,
  onLabelUpdate: _.noop,
  onDelete: _.noop,
  showSettings: true,
  showManageColumns: true,
  enableReorder: true,
  showDragHandle: true,
  enableInlineEdit: true,
  onSettingsClick: _.noop,
  onChildToggle: _.noop,
  showChildItems: true,
  showHeaderCheckbox: false,
  onHeaderToggle: _.noop,
  isHeaderChecked: false,
};

export default DragAndDropListBox;
